import { Component, ChangeDetectionStrategy } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";
import {
  TagSelectorComponent,
  TagOption,
} from "../tag-selector/tag-selector.component";

/**
 * Custom Formly field type for tag selection with badges.
 * Wraps the TagSelectorComponent for use in Formly forms.
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'targetGroups',
 *   type: 'tag-selector',
 *   props: {
 *     label: 'Target groups',
 *     options: [
 *       { value: 'International', label: 'International' }
 *     ]
 *   }
 * }
 * ```
 */
@Component({
  selector: "formly-tag-selector",
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, TagSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="flex flex-col gap-2">
      @if (props.label) {
        <span [class]="labelClass()">
          {{ props.label | translate }}
          @if (props.required) { <span class="text-mty-danger ml-0.5">*</span> }
        </span>
      }
      <div [class]="wrapClass()">
        <app-tag-selector
          [options]="tagOptions"
          [formControl]="formControl"
        ></app-tag-selector>
      </div>
      @if (props['maxAmount']) {
        <span [class]="counterClass()">
          {{ selectedCount() }} / {{ props['maxAmount'] }}
        </span>
      }
      @if (isInvalid()) {
        <span class="font-base text-[12px] text-mty-danger">
          {{ 'This field is required' | translate }}
        </span>
      }
    </div>
  `,
})
export class TagSelectorFieldType extends FieldType<FieldTypeConfig> {
  override defaultOptions = {
    props: {
      options: [] as TagOption[],
    },
  };

  get tagOptions(): TagOption[] {
    return Array.isArray(this.props.options) ? this.props.options : [];
  }

  selectedCount(): number {
    const v = this.formControl?.value;
    return Array.isArray(v) ? v.length : 0;
  }

  counterClass(): string {
    const max = this.props['maxAmount'] as number | undefined;
    const atCap = !!max && this.selectedCount() >= max;
    return atCap
      ? 'font-base text-xs text-mty-danger font-semibold'
      : 'font-base text-xs text-black/55';
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  labelClass(): string {
    const base = 'inline-block font-alternate text-xs font-semibold tracking-wider uppercase';
    return this.isInvalid()
      ? `${base} text-mty-danger`
      : `${base} text-[var(--mat-sys-on-surface-variant)]`;
  }

  wrapClass(): string {
    const base = 'rounded-2xl p-2 transition-colors';
    return this.isInvalid() ? `${base} bg-mty-danger/5 ring-1 ring-mty-danger/30` : base;
  }
}
